import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { css } from 'styled-components'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { IconChatOutline } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import SearchField from 'components/Form/SearchField/SearchField'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import HighLightText from 'features/HighLightText/HighLightText'
import { getSubscriptionModels } from 'services/endpoints/subscriptions'
import { useUserProfile } from 'store'

import styles from './_who-are-you-writing-to-modal.module.scss'

type SubscribedModelData = {
  user_id: number
  name: string
  username: string
  avatar?: string | null
  cover?: string | null
  conversation_id?: string | string | null
  active_stream?: boolean
  price?: number
  current_plan_price?: string
  discount?: number
  last_activity?: string
  status?: string
  expires?: string
  one_month_price?: string
  is_online?: boolean
  is_active?: boolean
  billing_cycle?: number
  total_months_subscribed?: number
  subscription_renew_date?: string
  new_posts?: number
}

type SubscriptionResponseItem = {
  model_data: SubscribedModelData
}

interface Props {
  closeFn: () => void
}

export const WhoAreYouWritingToFan = ({ closeFn }: Props) => {
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const unFocusRef = useRef<HTMLDivElement | null>(null)

  const userData = useUserProfile()
  const { t } = useTranslation()
  const [debouncedSearch, setDebouncedSearch] = useState('')


  const {
    data: subscriptionsData,
    isLoading,
    isFetching,
    isError,
  } = useQuery<SubscriptionResponseItem[]>(
    [
      'user-subscriptions',
      debouncedSearch
    ],
    async () =>
      await getSubscriptionModels(
        debouncedSearch || undefined
      ),
    {
      enabled: userData?.role === 'fan',
      staleTime: 60_000,
      cacheTime: 5 * 60_000,
      keepPreviousData: true,
    }
  )
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, 350)

    return () => clearTimeout(timeout)
  }, [search])

  const subs = useMemo(() => {
    return (subscriptionsData ?? [])
      .map((item) => item.model_data)
      .filter(Boolean)
  }, [subscriptionsData])

  const normalizedSearch = search.toLowerCase().trim()

  const filteredSubs = useMemo(() => {
    if (!normalizedSearch) return subs

    return subs.filter((user) => {
      const name = user.name?.toLowerCase() || ''
      const username = user.username?.toLowerCase() || ''

      return (
        name.includes(normalizedSearch) ||
        username.includes(normalizedSearch)
      )
    })
  }, [subs, normalizedSearch])

  return (
    <>
      <div className={styles.modal_container}>
        <div ref={unFocusRef} tabIndex={0} />

        <div
          className={styles.input_container}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setSearchOpen(false)}
          tabIndex={0}
        >
          <SearchField
            value={search}
            changeFn={setSearch}
            clearFn={() => setSearch('')}
            additionalProps={{
              placeholder: t('searchCreators'),
            }}
            customClass={`${styles.search_field} ${
              searchOpen && search.trim() !== ''
                ? styles.search_field_open
                : ''
            }`}
          />
        </div>

        <div className={styles.users_container_scroll}>
          {isLoading && (
            <div style={{ padding: 12, opacity: 0.7 }}>
              {t('loading', 'Loading...')}
            </div>
          )}

          {!isLoading && isError && (
            <div style={{ padding: 12 }}>
              {t('failedToLoadSubscriptions', 'Failed to load subscriptions.')}
            </div>
          )}

          {!isLoading && !isError && filteredSubs.length === 0 && (
            <div style={{ padding: 12, opacity: 0.7 }}>
              {normalizedSearch
                ? t('noMatches', 'No matches')
                : t('noSubscriptions', 'No subscriptions')}
            </div>
          )}

          {!isLoading &&
            !isError &&
              filteredSubs.map((user) => {
                const convId = user.conversation_id ?? null

                const chatState = {
                  otherUserData: {
                    id: user.user_id,
                    display_name: user.name,
                    username: user.username,
                    avatar: {
                      url: user.avatar ?? avatarPlaceholder,
                    },
                    conversation_id: user.conversation_id,
                    isSubscribed: true,
                  },
                  ...(convId ? { conversationId: convId } : {}),
                }

return (
  <Link
    key={user.user_id}
    to={`/chat/${user.user_id}`}
    state={chatState}
    onClick={closeFn}
    className={styles.user_card_link}
  >
    <ActionCard
      text={
        <HighLightText
          text={user.name || ''}
          highlight={search}
        />
      }
      description={
        <HighLightText
          text={`@${user.username || ''}`}
          highlight={search}
        />
      }
      avatar={
        <AvatarHolder
          img={user.avatar ?? avatarPlaceholder}
          size="50"
        />
      }
      customHtml={
        <div className="actionCard__fans__icons--chatOutline">
          <IconChatOutline color="#2894FF" />
        </div>
      }
      customStyle={css`
        padding: 1rem 1.25rem;
        margin: 0;
        box-shadow: none;
        border-radius: 2.5rem;
        cursor: pointer;
background: linear-gradient(
  89.85deg,
  #f8f8f8 0.1%,
  #fdfdfd 102.02%
);

        &:hover {
          background: #f4f4f4;
        }
      `}
    />
  </Link>
)
              })}
        </div>
      </div>
    </>
  )
}